import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import './TeamPage.css';
import { CTA, Footer } from './App';

/* ============================================
   SCROLL ANIMATION HOOK
   ============================================ */
function useScrollAnimation() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0, rootMargin: '0px 0px -50px 0px' });
    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div ref={ref} className={`fade-in ${isVisible ? 'visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

/* ============================================
   ICONS
   ============================================ */
const Icons = {
  ArrowRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
    </svg>
  ),
};

/* ============================================
   TEAM DATA
   --------------------------------------------
   Each member gets a card on /team and a full bio page at /team/:slug.

   • photo     — image in /public/images (or a remote URL). Falls back to
                 the member's initials when omitted.
   • cover     — optional wide banner image used behind the bio-page hero.
   • tagline   — one line shown on the card.
   • bio       — array of paragraphs for the detail page. Leave as [] to
                 show a "full biography coming soon" placeholder.
   • highlights— optional numbered list on the detail page.
   • email / linkedin / instagram / website — optional contact pills.

   To show empty "Reserved for future team member" cards in the grid, raise
   PLACEHOLDER_SLOTS below to a number larger than the member count.
   ============================================ */
const teamMembers = [
  {
    slug: 'darius-narcisse',
    name: 'Darius Narcisse',
    role: 'U.S. Army Veteran, SGT',
    photo: '/images/team-executive-director.jpg',
    bio: [],
    highlights: [],
    email: 'contact@mcsg.org',
  },
  {
    slug: 'eli-marine',
    name: 'Eli Marine',
    role: 'Lone Star College Student, Animal Science Major',
    photo: '/images/team-program-coordinator.jpg',
    bio: [],
    highlights: [],
    email: 'contact@mcsg.org',
  },
  {
    slug: 'ej-marine-iv',
    name: 'EJ Marine IV',
    role: 'U.S. Army (Active Duty), SSG',
    photo: '/images/team-outreach-specialist.jpg',
    bio: [],
    highlights: [],
    email: 'contact@mcsg.org',
  },
  {
    slug: 'ej-marine',
    name: 'EJ Marine',
    role: 'U.S. Army (Retired), SGT',
    photo: '/images/team-volunteer-coordinator.jpg',
    bio: [],
    highlights: [],
    email: 'contact@mcsg.org',
  },
  {
    slug: 'anthony-marquez',
    name: 'Anthony Marquez',
    role: 'U.S. Marine Corps (Retired), LCpl · Digital Media Strategist',
    photo: '/images/team-anthony-marquez.webp',
    cover: '/images/team-anthony-marquez-cover.webp',
    tagline: 'Houston-based filmmaker, Marine Corps veteran, and nonprofit founder building opportunity through media, storytelling, and community.',
    bio: [
      'Anthony A. Marquez is a Houston-based filmmaker, police officer, Marine Corps veteran, and nonprofit founder. Across law enforcement, logistics, and digital production, he blends technical skill, leadership, and creativity to build opportunities through media, storytelling, and community.',
      'He leads Mystic Media Film, a Texas-licensed, veteran-owned production and marketing company built to be a small business’s full media department in one team. The work spans cinematic video, brand systems, social media strategy, and business-focused content, with engagements ranging from commercial productions to law enforcement media initiatives and creative brand campaigns.',
      'As founder and president of Creator Terminal, an IRS-recognized 501(c)(3) nonprofit, Anthony is building a large-scale creative hub focused on accessible education in film, audio engineering, graphic design, coding, business administration, and 3D development. The vision centers on helping everyday people, from students to small business owners, gain access to tools, training, and creative opportunities that are often out of reach.',
      'In entertainment, he created MyShowBro, a federally trademarked, creator-driven media platform and YouTube network that has been in continuous use since 2013. MyShowBro is home to original storytelling, dark comedy, puppetry, filmmaking, and independent creator collaborations, and it serves as a launchpad for aspiring filmmakers.',
      'Before entering law enforcement and media full time, Anthony served honorably in the United States Marine Corps as a Motor Transport Operator, where he learned convoy operations, logistics, and leadership under pressure. That combination of discipline, operational thinking, and creative ambition still shapes the way he approaches both business and community building today.',
      'At his core, he is driven by a single belief: creativity, education, and storytelling can change the trajectory of people’s lives, especially for those who do not traditionally have access to those opportunities.',
    ],
    highlights: [
      'Honorably discharged United States Marine Corps veteran (Motor Transport Operator)',
      'Independent media production since 2013',
      'Sworn police officer',
      'Federal trademark holder, MYSHOWBRO® (U.S. Reg. No. 6,912,480)',
      'Founder and President, Creator Terminal (IRS-recognized 501(c)(3))',
      'Managing Member, Mystic Media Film LLC (Texas veteran-owned business)',
    ],
    email: 'sales@mysticmediafilm.com',
    instagram: 'https://instagram.com/myshowbro',
    youtube: 'https://youtube.com/@Myshowbro',
    discord: 'https://discord.com/invite/hdVMacjRdB',
  },
];

/* Total cards rendered in the grid (real members + dashed placeholders).
   With 4 members and 6 slots, two "Reserved" cards fill out the bottom row.
   Set this equal to the member count to hide all placeholders. */
const PLACEHOLDER_SLOTS = 6;

const getInitials = (name) =>
  name.split(' ').map((p) => p[0]?.toUpperCase() ?? '').slice(0, 2).join('');

/* ============================================
   TEAM CARD
   Real members render a photo (or initials) and link to their bio page.
   Empty slots render a dashed "Reserved" placeholder.
   ============================================ */
function TeamCard({ member, placeholderIndex }) {
  if (!member) {
    return (
      <div className="tp-card-reserved">
        <span className="tp-reserved-slot">Slot {String(placeholderIndex).padStart(2, '0')}</span>
        <span className="tp-reserved-label">Reserved for future<br />team member</span>
      </div>
    );
  }

  return (
    <a href={`/team/${member.slug}`} className="tp-card">
      {member.photo
        ? <div className="tp-card-photo" style={{ backgroundImage: `url(${member.photo})` }} />
        : <div className="tp-card-photo tp-card-photo-empty"><span className="tp-card-initials">{getInitials(member.name)}</span></div>}
      <div className="tp-card-overlay" />
      <div className="tp-card-body">
        <span className="tp-card-role">{member.role}</span>
        <span className="tp-card-name">{member.name}</span>
        {member.tagline && <span className="tp-card-tagline">{member.tagline}</span>}
      </div>
    </a>
  );
}

/* ============================================
   TEAM PAGE
   ============================================ */
function TeamPage() {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Volunteer Application - ${formData.firstName} ${formData.lastName}`);
    const body = encodeURIComponent(`First Name: ${formData.firstName}\nLast Name: ${formData.lastName}\nEmail: ${formData.email}\nPhone: ${formData.phone}`);
    window.location.href = `mailto:contact@mcsg.org?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const placeholderCount = Math.max(0, PLACEHOLDER_SLOTS - teamMembers.length);

  return (
    <div className="team-page">
      <Helmet>
        <title>Our Team | Mission Critical Support Group - Katy, TX Nonprofit</title>
        <meta name="description" content="Meet the dedicated team behind Mission Critical Support Group, a 501(c)(3) nonprofit in Katy, TX. Our staff serves veterans and supports survivors of domestic violence in Fort Bend County and Houston." />
        <link rel="canonical" href="https://www.missioncriticalsupportgroup.com/team" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.missioncriticalsupportgroup.com/team" />
        <meta property="og:title" content="Our Team | Mission Critical Support Group" />
        <meta property="og:description" content="Meet the dedicated team serving veterans and supporting survivors of domestic violence at MCSG in Katy, TX." />
        <meta property="og:site_name" content="Mission Critical Support Group" />
        <meta property="og:image" content="https://www.missioncriticalsupportgroup.com/images/og-share.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="The team behind Mission Critical Support Group, a 501(c)(3) nonprofit in Katy, TX." />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Team | Mission Critical Support Group" />
        <meta name="twitter:description" content="Meet the dedicated team serving veterans and supporting survivors of domestic violence at MCSG in Katy, TX." />
        <meta name="twitter:image" content="https://www.missioncriticalsupportgroup.com/images/og-share.jpg" />
      </Helmet>

      {/* Navigation */}
      <nav className="team-nav">
        <div className="team-nav-inner">
          <a href="/" className="team-nav-back"><Icons.ArrowLeft /><span>Back to Home</span></a>
        </div>
      </nav>

      {/* Hero */}
      <section className="team-hero">
        <AnimatedSection>
          <div className="team-hero-label">Our Team</div>
          <h1 className="team-hero-title">Meet the People Behind the Mission</h1>
          <p className="team-hero-description">
            Our team is made up of dedicated individuals in Katy, TX who believe in serving those who have served
            and protecting survivors of domestic violence. Together, we work to restore hope and rebuild lives
            across Fort Bend County and the greater Houston area.
          </p>
        </AnimatedSection>
      </section>

      {/* Team Grid */}
      <section className="team-cards-section">
        <AnimatedSection>
          <div className="tp-grid-head">
            <div className="tp-grid-head-label"><span className="line" />The Team</div>
            <p className="tp-grid-head-hint">Click any team member to read their full background.</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.05}>
          <div className="tp-grid">
            {teamMembers.map((member) => (
              <TeamCard key={member.slug} member={member} />
            ))}
            {Array.from({ length: placeholderCount }).map((_, i) => (
              <TeamCard key={`placeholder-${i}`} placeholderIndex={teamMembers.length + i + 1} />
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Join/Volunteer Section */}
      <section className="team-join-section">
        <div className="team-join-inner">
          <AnimatedSection>
            <div className="team-join-content">
              <h2>Volunteer With Mission Critical Support Group</h2>
              <p>
                Whether you want to volunteer in Katy, partner with us, or join our team, your time and
                passion help us serve veterans and protect survivors of domestic violence. Every contribution makes
                our mission stronger across Fort Bend County and Houston.
              </p>

              <form className="team-join-form" onSubmit={handleSubmit}>
                <div className="tj-form-row">
                  <div className="tj-form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input id="firstName" type="text" placeholder="First name" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} required />
                  </div>
                  <div className="tj-form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input id="lastName" type="text" placeholder="Last name" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} required />
                  </div>
                </div>
                <div className="tj-form-group">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="tj-form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input id="phone" type="tel" placeholder="(123) 456-7890" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                </div>
                <button type="submit" className="tj-submit-btn">
                  {submitted ? 'Opening Email Client...' : 'Get Involved'}
                  {!submitted && <Icons.ArrowRight />}
                </button>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

export { teamMembers };
export default TeamPage;
